import { useTranslation } from "react-i18next";
import { Control } from "react-hook-form";
import { IconButton } from "@carbon/react";
import { TrashCan } from "@carbon/icons-react";
import TextField from "../carbon/Form/TextField";
import SelectField from "../carbon/Form/SelectField";
import { ContactsFormFields } from "./contactsInformationValidationSchema";
import { CompanyContactType } from "../../types";

export interface ContactRowProps {
  index: number;
  control: Control<ContactsFormFields>;
  onRemove: () => void;
}

const CONTACT_TYPE_VALUES = [
  CompanyContactType.FINANCIAL,
  CompanyContactType.QUALITY,
  CompanyContactType.LEGAL_REPRESENTATIVE,
  CompanyContactType.COMMERCIAL,
  CompanyContactType.LOGISTICS,
] as const;

const CONTACT_TYPE_LABEL_KEYS: Record<CompanyContactType, string> = {
  [CompanyContactType.FINANCIAL]: "companyProfile.financialContact",
  [CompanyContactType.QUALITY]: "companyProfile.qualityContact",
  [CompanyContactType.LEGAL_REPRESENTATIVE]:
    "companyProfile.legalRepresentative",
  [CompanyContactType.COMMERCIAL]: "companyProfile.commercialContact",
  [CompanyContactType.LOGISTICS]: "companyProfile.logisticsContact",
};

/**
 * One row of `ContactsInformationForm`'s `useFieldArray` — same "compose `carbon/Form/*`
 * primitives per row, no new shared row primitive" pattern `AddressRow.tsx`/`BankingRow.tsx`
 * established (Tasks 8-9). `type` is a `SelectField` (per this task's brief); everything else is
 * `TextField`.
 *
 * Real, disclosed behaviour change from the pre-Carbon form, same directional change Task 8 made
 * for addresses: every contact — already-persisted or freshly added — is now editable in place
 * (the pre-Carbon form only ever let you ADD new rows or remove an unsaved one; an already-saved
 * contact rendered as a read-only card with a coloured type badge, never editable again). Safe
 * for the identical reason Task 8 established: `CompanyService#updateCompanyContacts` (backend)
 * already fully REPLACES the contacts list on every save.
 */
const ContactRow = ({ index, control, onRemove }: ContactRowProps) => {
  const { t } = useTranslation();

  const typeOptions = CONTACT_TYPE_VALUES.map((value) => ({
    value,
    label: t(CONTACT_TYPE_LABEL_KEYS[value]),
  }));

  return (
    <div className="contacts-information-form__row">
      <div className="contacts-information-form__row-grid">
        <TextField
          name={`contacts.${index}.name`}
          label={t("companyProfile.contactName")}
          control={control}
        />
        <TextField
          name={`contacts.${index}.email`}
          label={t("companyProfile.contactEmail")}
          control={control}
          type="email"
        />
        <TextField
          name={`contacts.${index}.phone`}
          label={t("companyProfile.contactPhone")}
          control={control}
          type="tel"
        />
        <TextField
          name={`contacts.${index}.position`}
          label={t("companyProfile.contactPosition")}
          control={control}
        />
        <SelectField
          name={`contacts.${index}.type`}
          label={t("companyProfile.selectContactType")}
          control={control}
          options={typeOptions}
          placeholder={t("companyProfile.selectContactType")}
        />
      </div>

      <div className="contacts-information-form__row-actions">
        <IconButton
          kind="ghost"
          label={t("cta.remove")}
          align="top"
          onClick={onRemove}
        >
          <TrashCan />
        </IconButton>
      </div>
    </div>
  );
};

export default ContactRow;
